import { z } from 'zod';

/*
 * The database returns codes, never sentences (ADR 0009). The apps translate them
 * through their es-CO files. Keep these lists in sync with the SQL that raises them.
 */

/** Values of `warnings` returned by `public.recommend()`. */
export const RECOMMENDATION_WARNINGS = [
  'confirm_allergens_with_place',
  'unverified_location_or_status',
  'reference_price',
] as const;
export const RecommendationWarningSchema = z.enum(RECOMMENDATION_WARNINGS);
export type RecommendationWarning = z.infer<typeof RecommendationWarningSchema>;

/** Values the database puts in the `hint` of an error it raises on purpose. */
export const DATABASE_ERROR_HINTS = [
  'auth_required',
  'dish_invalid',
  'evidence_already_discarded',
  'evidence_empty',
  'evidence_not_discardable',
  'evidence_not_found',
  'evidence_owner_only',
  'field_policy_locked',
  'fsq_id_locked',
  'health_consent_required',
  'invalid_coordinates',
  'menu_already_added',
  'menu_changed',
  'menu_link_only',
  'menu_not_found',
  'menu_url_invalid',
  'menu_url_is_platform',
  'pilot_place_linked',
  'pilot_place_not_found',
  'pilot_place_taken',
  'pilot_swap_invalid',
  'pilot_swap_needs_not_in_fsq',
  'pilot_swap_reserve_not_in_fsq',
  'place_closed',
  'place_in_other_zone',
  'place_not_found',
  'place_not_published',
  'place_without_fsq_id',
  'profile_required',
  'publish_requires_verification',
  'reason_required',
  'refresh_limit_reached',
  'report_limit_reached',
  'restriction_tag_mismatch',
  'source_identity_locked',
  'source_not_storable',
  'suggestion_limit_reached',
  'suggestion_not_open',
  'team_account_not_invited',
  'team_only',
  'visit_limit_reached',
] as const;
export const DatabaseErrorHintSchema = z.enum(DATABASE_ERROR_HINTS);
export type DatabaseErrorHint = z.infer<typeof DatabaseErrorHintSchema>;

/**
 * Reads the hint code from a PostgREST error (`{ hint: 'report_limit_reached', ... }`).
 * Returns null for any error the database did not raise on purpose.
 */
export function getDatabaseErrorHint(error: unknown): DatabaseErrorHint | null {
  if (typeof error !== 'object' || error === null || !('hint' in error)) {
    return null;
  }
  const parsed = DatabaseErrorHintSchema.safeParse(error.hint);
  return parsed.success ? parsed.data : null;
}
